import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Breakpoint,
  SxProps,
  Theme,
  TypographyProps,
} from '@mui/material'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { TLGTypography } from '@components/TLGTypography'

import {
  accordionDetailsSx,
  accordionSummarySx,
  accordionSx,
  expandMoreIconSx,
} from './styles'

export interface ITLGAccordionProps extends Omit<AccordionProps, 'title'> {
  title: string | React.ReactNode
  defaultExpanded?: boolean
  accordionSx?: SxProps<Theme>
  accordionDetailsSx?: SxProps<Theme>
  accordionSummarySx?: SxProps<Theme>
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void
  expanded?: boolean
  dataTestId?: string
  responsiveVariantTitle?: Partial<
    Record<Breakpoint, TypographyProps['variant']>
  >
  isBeforeExpanded?: boolean
  isAfterExpanded?: boolean
}

const TLGAccordion = ({
  title,
  defaultExpanded,
  onChange,
  dataTestId,
  expanded,
  children,
  isBeforeExpanded = false,
  isAfterExpanded = false,
  accordionSx: overrideAccordionSx,
  accordionDetailsSx: overrideAccordionDetailsSx,
  accordionSummarySx: overrideAccordionSummarySx,
  responsiveVariantTitle: overrideResponsiveVariantTitle,
  ...rest
}: ITLGAccordionProps) => (
  <Accordion
    onChange={onChange}
    expanded={expanded}
    defaultExpanded={defaultExpanded}
    elevation={1}
    sx={
      {
        ...accordionSx({ isBeforeExpanded, isAfterExpanded }),
        ...overrideAccordionSx,
      } as SxProps<Theme>
    }
    data-testid={
      dataTestId ? `${dataTestId}-accordion-section` : 'accordion-section'
    }
    {...rest}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={expandMoreIconSx} />}
      sx={
        {
          ...accordionSummarySx,
          ...overrideAccordionSummarySx,
        } as SxProps<Theme>
      }
    >
      <TLGTypography
        data-testid={
          dataTestId ? `${dataTestId}-accordion-title` : 'accordion-title'
        }
        responsiveVariant={{
          xs: 'h6',
          md: 'h5',
          ...overrideResponsiveVariantTitle,
        }}
      >
        {title}
      </TLGTypography>
    </AccordionSummary>
    <AccordionDetails
      sx={
        { accordionDetailsSx, ...overrideAccordionDetailsSx } as SxProps<Theme>
      }
      data-testid={
        dataTestId ? `${dataTestId}-accordion-details` : 'accordion-details'
      }
    >
      {children}
    </AccordionDetails>
  </Accordion>
)

export { TLGAccordion }
